import { useState, useEffect, useCallback } from 'react'
import { useMessageApi } from './use-message-api'
import { useMessageComponents } from '../components/message-components-provider'

/**
 * Hook to get message count for a specific entity
 * 
 * @param entityTypeId - The type of entity (e.g., "consultant", "client")  
 * @param entityId - The unique identifier for the entity
 * @returns Object with count, loading state, and error state
 * 
 * @example
 * ```tsx
 * function CustomChatTrigger({ entityTypeId, entityId }) {
 *   const { count, isLoading, error } = useMessageCount(entityTypeId, entityId)
 *   
 *   return (
 *     <button>
 *       Chat {count > 0 && <Badge>{count}</Badge>}
 *     </button>
 *   )
 * }
 * ```
 */
export function useMessageCount(entityTypeId: string, entityId: string) {
  const [count, setCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  
  const apiService = useMessageApi()
  const { config } = useMessageComponents()
  const { userToken } = config

  const fetchCount = useCallback(async () => {
    if (!userToken) {
      setCount(0)
      setError(null)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const messageCount = await apiService.fetchMessageCountWithToken(entityTypeId, entityId)
      setCount(messageCount)
    } catch (err) {
      // Error is already logged in the service
      setError(err instanceof Error ? err.message : 'Failed to fetch message count')
      setCount(0)
    } finally {
      setIsLoading(false)
    }
  }, [entityTypeId, entityId, userToken, apiService])

  useEffect(() => {
    fetchCount()
  }, [fetchCount])

  return {
    /** Current message count for the entity */
    count,
    /** Whether the count is currently being fetched */
    isLoading,
    /** Error message if count fetch failed, null if no error */
    error,
    /** Helper to manually refresh the count */
    refresh: fetchCount,
  }
}
